import { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { User } from '@pletly/shared';
import { Button, DefaultLayout, Table, Text } from '@pletly/ui';
import { RootStackScreenProps } from '../../../navigation/types';
import { useAllTermsOfUse } from './components/useAllTermsOfUse';
import { MembersAcceptedModal } from './components/MembersAcceptedModal';
import { withTranslation } from '../../../locales/hoc';

type Props = RootStackScreenProps<'AllTermsOfUse'>;

export const AllTermsOfUsePage = withTranslation(
	({
		route: {
			params: { uuid },
		},
		navigation: { navigate },
	}: Props) => {
		const { t } = useTranslation();
		const { data } = useAllTermsOfUse({ organisationUuid: uuid });
		const [selectedTermOfUseMembers, setSelectedTermOfUseMembers] = useState<User[]>([]);

		return (
			<DefaultLayout view>
				<View style={styles.header}>
					<Text category="h1">{t('terms-of-use.title')}</Text>
					<Button
						color="primary"
						variant="filled"
						onPress={() => navigate('NewTermsOfUse', { uuid })}
					>
						{t('terms-of-use.new-version')}
					</Button>
				</View>

				<Table
					data={data?.organisation.allTermsOfUse || []}
					columnsConfig={[
						{
							name: t('terms-of-use.table.updated-at'),
							renderCell: ({ row }) => <Text>{new Date(row.updatedAt).toLocaleString()}</Text>,
						},
						{
							name: t('terms-of-use.table.members-accepted'),
							renderCell: ({ row }) => (
								<Button
									size="small"
									variant="filled"
									disabled={!row.membersAccepted.length}
									onPress={() => {
										if (!row.membersAccepted.length) return;

										setSelectedTermOfUseMembers(
											row.membersAccepted.flatMap((member) => member.user),
										);
									}}
								>
									<Text>
										{t('terms-of-use.accepted')} ({row.membersAccepted.length})
									</Text>
								</Button>
							),
						},
						{
							name: t('terms-of-use.table.text'),
							renderCell: ({ row }) => (
								<Pressable
									style={styles.textCell}
									onPress={() =>
										navigate('TermsOfUseView', {
											uuid: row.uuid,
											text: row.text,
											updatedAt: new Date(row.updatedAt).toLocaleString(),
										})
									}
								>
									<Text numberOfLines={4}>{row.text}</Text>
								</Pressable>
							),
						},
					]}
					style={styles.table}
				/>

				<MembersAcceptedModal
					visible={!!selectedTermOfUseMembers.length}
					onClose={() => setSelectedTermOfUseMembers([])}
					contacts={selectedTermOfUseMembers}
				/>
			</DefaultLayout>
		);
	},
);

const styles = StyleSheet.create({
	header: {
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
	table: {
		width: '100%',
	},
	textCell: {
		flex: 1,
	},
});
